/* eslint-disable unicorn/no-null -- Tweet cache relies on `null` markers for fallback stores and cache miss handling. */

import { deserializeDevalue, serializeDevalue } from '@sweetistics/trpc-shared';
import { uniq } from 'es-toolkit';
import type { RxCollection, RxJsonSchema } from 'rxdb';
import { getRxdbCollection, registerLocalCacheCollection } from '@/lib/cache/databases/local-cache.db';
import { registerDebugNamespace } from '@/lib/debug/registerDebugNamespace';
import type { Tweet } from '@/types/tweet';

const COLLECTION_ID = 'tweet-cache';
export const TWEET_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface TweetCacheRecord extends Record<string, unknown> {
  fetchedAt: number;
  id: string;
  tweet: string;
}

interface TweetCacheFallbackRecord {
  fetchedAt: number;
  id: string;
  tweet: Tweet;
}

type TweetCacheFallbackStore = Map<string, TweetCacheFallbackRecord>;

declare global {
  // Node/RSC environments fall back to an in-memory Map so we can prime/serve cached tweets without Dexie.
  // eslint-disable-next-line no-var
  var __sweetistics_tweet_cache_fallback__: TweetCacheFallbackStore | undefined;
}

type TweetCacheGlobal = typeof globalThis & {
  __SWEETISTICS_FORCE_MEMORY_RXDB__?: boolean;
};

const cacheRuntime = globalThis as TweetCacheGlobal;
const shouldUseFallbackStore = cacheRuntime.window === undefined;

// SSR/RSC loaders and Vitest do not have IndexedDB, so we swap in a tiny in-memory Map to keep
// cache helpers functional. Browser builds keep using RxDB/Dexie.

function getFallbackStore(): TweetCacheFallbackStore | null {
  if (!shouldUseFallbackStore) {
    return null;
  }
  const existingStore = globalThis.__sweetistics_tweet_cache_fallback__;
  if (existingStore) {
    return existingStore;
  }
  const fallback = new Map<string, TweetCacheFallbackRecord>();
  globalThis.__sweetistics_tweet_cache_fallback__ = fallback;
  return fallback;
}

const DEBUG_NAMESPACE = 'tweetCache';
if (globalThis.window !== undefined) {
  registerDebugNamespace(DEBUG_NAMESPACE, {});
}
const log = console === undefined ? null : console;
const shouldLogTweetCache = () => {
  const windowObject = globalThis.window;
  if (windowObject === undefined) return false;
  const dbg = (windowObject as Window & { __sweetisticsDebug?: Record<string, unknown> }).__sweetisticsDebug;
  // biome-ignore lint/complexity/useLiteralKeys: debug payload lives on a loose index signature.
  return Boolean(dbg?.['tweetCache']);
};

const TweetCacheRxSchema: RxJsonSchema<TweetCacheRecord> = {
  title: 'tweet-cache-record',
  version: 0,
  type: 'object',
  primaryKey: {
    key: 'id',
    fields: ['id'],
    separator: '||',
  },
  properties: {
    id: { type: 'string', maxLength: 64 },
    tweet: { type: 'string', maxLength: 200_000 },
    fetchedAt: { type: 'number' },
  },
  required: ['id', 'tweet', 'fetchedAt'],
  additionalProperties: false,
};

const isTweetLike = (value: unknown): value is Tweet =>
  Boolean(
    value && typeof value === 'object' && !Array.isArray(value) && typeof (value as { id?: unknown }).id === 'string'
  );

function collectTweetGraph(source: Iterable<Tweet>): Tweet[] {
  const queue: Tweet[] = [];
  for (const tweet of source) {
    if (isTweetLike(tweet)) {
      queue.push(tweet);
    }
  }

  const visited = new Map<string, Tweet>();
  while (queue.length > 0) {
    const current = queue.pop();
    if (!current || visited.has(current.id)) {
      continue;
    }
    visited.set(current.id, current);
    if (isTweetLike(current.quotedTweet)) {
      queue.push(current.quotedTweet);
    }
    if (isTweetLike(current.retweetedTweet)) {
      queue.push(current.retweetedTweet);
    }
  }

  return [...visited.values()];
}

const serializeTweetPayload = (tweet: Tweet): string => {
  const serialized = serializeDevalue(tweet);
  if (typeof serialized === 'string') {
    return serialized;
  }
  return JSON.stringify(tweet);
};

const cloneTweetRecord: (value: Tweet) => Tweet = (value) => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(value);
  }
  const parsed = deserializeTweetFromStorage(serializeTweetPayload(value));
  return parsed ?? value;
};

function serializeTweetForStorage(tweet: Tweet): string {
  return serializeTweetPayload(tweet);
}

function deserializeTweetFromStorage(payload: unknown): Tweet | null {
  if (typeof payload !== 'string' || payload.length === 0) {
    return null;
  }
  try {
    const parsed = deserializeDevalue(payload);
    if (isTweetLike(parsed)) {
      return parsed;
    }
  } catch {
    /* ignore corrupted payload */
  }
  return null;
}

async function pruneExpiredRecords(collection: RxCollection<TweetCacheRecord>): Promise<void> {
  const fallbackStoreInstance = getFallbackStore();
  if (fallbackStoreInstance) {
    const cutoff = Date.now() - TWEET_CACHE_TTL_MS;
    for (const [key, record] of fallbackStoreInstance.entries()) {
      if (record.fetchedAt < cutoff) {
        fallbackStoreInstance.delete(key);
      }
    }
    return;
  }
  const docs = await collection.find().exec();
  if (!Array.isArray(docs) || docs.length === 0) {
    return;
  }
  const cutoff = Date.now() - TWEET_CACHE_TTL_MS;
  const stale = docs.filter((document_) => {
    try {
      const data = document_.toJSON();
      return typeof data.fetchedAt === 'number' && data.fetchedAt < cutoff;
    } catch {
      return false;
    }
  });
  if (stale.length === 0) {
    return;
  }
  await Promise.all(
    stale.map(async (document_) => {
      try {
        await document_.remove();
      } catch {
        /** swallow removal failures – best effort */
      }
    })
  );
  log?.debug?.('[tweet-cache] pruned expired tweets', stale.length);
}

let tweetCacheCollection: RxCollection<TweetCacheRecord> | null = null;

if (!shouldUseFallbackStore) {
  const { rxCollection } = await registerLocalCacheCollection<TweetCacheRecord>(COLLECTION_ID, {
    schema: TweetCacheRxSchema,
    prune: async ({ rxCollection: collection }) => {
      await pruneExpiredRecords(collection);
    },
    startSync: !cacheRuntime.__SWEETISTICS_FORCE_MEMORY_RXDB__,
  });
  await pruneExpiredRecords(rxCollection);
  tweetCacheCollection = rxCollection;
}

function getTweetCacheCollection(): RxCollection<TweetCacheRecord> {
  if (!tweetCacheCollection) {
    throw new Error('Tweet cache collection unavailable when fallback store is active');
  }
  return tweetCacheCollection;
}

export async function getTweetCacheEntries(ids: Iterable<string>): Promise<Map<string, Tweet>> {
  const result = new Map<string, Tweet>();
  const uniqueIds = uniq([...ids].map((id) => id.trim()).filter((id) => id.length > 0));
  if (uniqueIds.length === 0) {
    return result;
  }

  const fallbackStoreInstance = getFallbackStore();
  if (fallbackStoreInstance) {
    const now = Date.now();
    for (const id of uniqueIds) {
      const record = fallbackStoreInstance.get(id);
      if (!record) {
        continue;
      }
      if (now - record.fetchedAt > TWEET_CACHE_TTL_MS) {
        fallbackStoreInstance.delete(id);
        continue;
      }
      result.set(id, record.tweet);
    }
    return result;
  }

  const removals: Promise<void>[] = [];
  const now = Date.now();

  const tweetCacheRxCollection = getTweetCacheCollection();

  await Promise.all(
    uniqueIds.map(async (id) => {
      try {
        const document_ = await tweetCacheRxCollection.findOne(id).exec();
        if (!document_) {
          return;
        }
        const data = document_.toJSON();
        if (typeof data.fetchedAt !== 'number' || now - data.fetchedAt > TWEET_CACHE_TTL_MS) {
          removals.push(
            document_
              .remove()
              .then(() => {
                return;
              })
              .catch(() => {
                /** swallow removal failures – best effort */
                return;
              })
          );
          return;
        }
        const tweetCandidate = deserializeTweetFromStorage(data.tweet);
        if (tweetCandidate) {
          result.set(id, tweetCandidate);
        }
      } catch {
        /** ignore read failures */
      }
    })
  );

  if (removals.length > 0) {
    await Promise.all(removals);
  }

  return result;
}

export async function primeTweetCacheFromTweets(tweets: Iterable<Tweet>, fetchedAt = Date.now()): Promise<void> {
  const graph = collectTweetGraph(tweets);
  if (graph.length === 0) {
    return;
  }

  const fallbackStoreInstance = getFallbackStore();
  if (fallbackStoreInstance) {
    for (const tweet of graph) {
      fallbackStoreInstance.set(tweet.id, { id: tweet.id, tweet: cloneTweetRecord(tweet), fetchedAt });
    }
    log?.debug?.('[tweet-cache] fallback prime', graph.length);
    return;
  }

  const documents: TweetCacheRecord[] = graph.map((tweet) => ({
    id: tweet.id,
    tweet: serializeTweetForStorage(tweet),
    fetchedAt,
  }));
  const tweetCacheRxCollection = getTweetCacheCollection();

  try {
    await tweetCacheRxCollection.bulkUpsert(documents);
    if (shouldLogTweetCache()) {
      log?.debug?.('[tweet-cache] bulkUpsert', documents.length);
    }
  } catch (error) {
    log?.warn?.('[tweet-cache] bulkUpsert failed', error);
  }
}

export async function getTweetFromCache(id: string): Promise<Tweet | null> {
  const entries = await getTweetCacheEntries([id]);
  return entries.get(id) ?? null;
}

export async function invalidateTweetCacheEntry(id: string): Promise<void> {
  const fallbackStoreInstance = getFallbackStore();
  if (fallbackStoreInstance) {
    fallbackStoreInstance.delete(id);
    return;
  }
  try {
    const tweetCacheRxCollection = getTweetCacheCollection();
    const document_ = await tweetCacheRxCollection.findOne(id).exec();
    if (document_) {
      await document_.remove();
    }
  } catch {
    /** ignore */
  }
}

export async function resetTweetCacheForTests(): Promise<void> {
  const fallbackStoreInstance = getFallbackStore();
  if (fallbackStoreInstance) {
    fallbackStoreInstance.clear();
    return;
  }
  const collection = await getRxdbCollection<TweetCacheRecord>(COLLECTION_ID);
  const docs = await collection.find().exec();
  await Promise.all(
    docs.map(async (document_) => {
      try {
        await document_.remove();
      } catch {
        /** ignore */
      }
    })
  );
}
